<template>
  <available-section section="task-module" style="width: 100%">
    <main class="task-list">
      <div class="task-list__body">
        <div class="task-list__header">
          <h1 class="task-list__title">Задачи </h1>
          <button
            class="task-list__button-add"
            type="button"
            v-on:click="showTaskCreationForm"
          >
            Добавить задачу
          </button>
        </div>
        <div class="task-list__filters">
          <div class="task-list__filters__selects">
            <v-autocomplete
              v-model="filter.authorId"
              class="task-list__autocomplete"
              clearable
              hide-details
              attach
              item-text="title"
              item-value="value"
              label="Автор"
              v-bind:items="filterPerformerList"
              v-on:input="updateTaskFilter"
            >
              <template v-slot:no-data>
                <p style="padding: 10px; margin:0;">Результатов нет</p>
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filter.performerId"
              class="task-list__autocomplete task-list__autocomplete--executor"
              clearable
              hide-details
              attach
              item-text="title"
              item-value="value"
              label="Исполнитель"
              v-bind:items="filterPerformerList"
              v-on:input="updateTaskFilter"
            >
              <template v-slot:no-data>
                <p style="padding: 10px; margin:0;">Результатов нет</p>
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filter.patientId"
              class="task-list__autocomplete task-list__autocomplete--patient"
              clearable
              hide-details
              attach
              item-text="title"
              item-value="value"
              label="Пациент"
              v-bind:items="filterPatientList"
              v-bind:no-filter=true
              v-on:input="updateTaskFilter"
              v-on:update:search-input="onSearchOptionInput"
            >
              <template v-slot:no-data>
                <p style="padding: 10px; margin:0;">Результатов нет</p>
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filter.roleName"
              class="task-list__autocomplete task-list__autocomplete--role"
              clearable
              hide-details
              attach
              item-text="title"
              item-value="slug"
              label="Роль"
              v-bind:items="filterRoleList"
              v-bind:no-filter=true
              v-on:input="updateTaskFilter"
              v-on:update:search-input="onSearchOptionInput"
            >
              <template v-slot:no-data>
                <p style="padding: 10px; margin:0;">Результатов нет</p>
              </template>
            </v-autocomplete>
          </div>
          <div class="task-list__filters__dates">
            <div>
              <span class="date-range-title">Дата создания</span>
              <date-range-with-presets
                v-model="filter.createPeriod"
                v-bind:is-show-button-clear="!!filter.createPeriod.length"
                v-bind:today="$options.today"
                v-on:input="updateTaskFilter"
              />
            </div>
            <div>
              <span class="date-range-title">Дедлайн</span>
              <date-range-with-presets
                v-model="filter.deadlinePeriod"
                v-bind:is-show-button-clear="!!filter.deadlinePeriod.length"
                v-bind:today="$options.today"
                v-on:input="updateTaskFilter"
              />
            </div>
          </div>
        </div>
        <div class="task-list__groups">
          <label
            v-for="(status) in taskStatusGroup"
            v-bind:key="status.title"
            class="task-list__group"
          >
            <input
              v-bind:id="status.title"
              v-model="selectedTaskStatus"
              hidden
              type="radio"
              v-bind:value="status.title"
              v-on:change="fetchTaskList()"
            />
            <span
              class="task-list__header__group__label"
              v-bind:class="status.title"
            >
              {{ status.title === 'all' ? 'Все' : mapStatus(status.title) }} {{ status.count }}
            </span>
          </label>
        </div>
        <side-slide-transition>
          <confirm-side-dialog
            v-if="isTaskCreationFormOpen"
            label="Добавить задачу"
            v-bind:need-confirm="needConfirmCreationForm"
            v-on:cancel="closeTaskCreationForm"
          >
            <form-task-creation
              v-bind:initial-performers="performerList"
              v-bind:patients="patientList"
              v-bind:roles-options="roleOptions"
              v-bind:violations="createViolations"
              v-on:cancel="closeTaskCreationForm"
              v-on:create-task="onSendCreateRequest"
              v-on:task-is-changed="needConfirmCreationForm = $event"
              v-on:search-input="onSearchOptionInput"
            />
          </confirm-side-dialog>
        </side-slide-transition>
        <side-slide-transition>
          <task-display
            v-if="isTaskDisplayOpen"
            v-bind:task="selectedTask"
            v-bind:violations="commentViolations"
            v-on:close="isTaskDisplayOpen = false"
            v-on:edit-task="showEditingForm"
            v-on:delete-task="onSendDeleteRequest"
            v-on:complete-task="onChangeStatus($event, 'completed')"
            v-on:submit-comment="onSubmitComment"
          />
        </side-slide-transition>
        <side-slide-transition>
          <confirm-side-dialog
            v-if="isTaskEditingOpen"
            label="Редактировать задачу"
            v-bind:need-confirm="needConfirmEditingForm"
            v-on:cancel="closeEditingForm"
          >
            <form-task-editing
              v-bind:initial-performers="performerList"
              v-bind:initial-task="editingTask"
              v-bind:patients="patientList"
              v-bind:roles-options="roleOptions"
              v-bind:violations="editViolations"
              v-on:cancel="closeEditingForm"
              v-on:edit-task="onSendEditRequest"
              v-on:task-is-changed="needConfirmEditingForm = $event"
              v-on:search-input="onSearchOptionInput"
            />
          </confirm-side-dialog>
        </side-slide-transition>
        <task-table
          v-bind:is-await-request="isAwaitRequest"
          v-bind:sort="sortOrder"
          v-bind:task-list="filteredTaskList"
          v-on:show-task="showTaskDisplay"
          v-on:change-status="onChangeStatus"
          v-on:sort-task-list="onSortTaskList"
        />
      </div>
    </main>
  </available-section>
</template>

<script>
import TaskTable from '@/components/TaskTable.vue';
import notifierMixin from '@/mixins/notifierMixin';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import FormTaskCreation from '@/components/FormTaskCreation.vue';
import FormTaskEditing from '@/components/FormTaskEditing.vue';
import TaskDisplay from '@/components/TaskDisplay.vue';
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';
import { ACTIVE_TASK_COUNT, STORE_TASK_FILTER } from '@/store/actions/tasks';
import { mapStatus, ViewTaskListRequest } from '@/storage/gateways/TaskGateway';
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import debounce from 'lodash.debounce';
import searchPatientMixin from '@/mixins/searchPatientMixin';

/**
 * Миксин редактирования задачи
 */
const taskEditingMixin = {
  data() {
    return {
      /** Открыта ли форма редактирования? */
      isTaskEditingOpen: false,
      /** Нужно подтверждение для закрытия формы редактирвания */
      needConfirmEditingForm: false,
      /** Редактируемая задача */
      editingTask: {},
      /** Ошибки при редактировании */
      editViolations: {},
      /** Ошибки комментариев */
      commentViolations: {},
    };
  },
  methods: {
    /**
     * @description Отправить запрос на редактирование
     * @param {TaskRequest} request
     * @param {Number}      id
     */
    onSendEditRequest(request, id) {
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.isTaskEditingOpen = false;
        this.editViolations = {};
        this.fetchTaskList();
        this.fetchActiveTaskCount();
      };
      const onFail = (error) => {
        this.editViolations = error.allCauses();
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.editTask(request, id)
        .then((result) => {
          result.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Отправить запрос на удаление
     * @param {number} id Идентификатор задачи
     */
    onSendDeleteRequest(id) {
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.isTaskDisplayOpen = false;
        this.fetchTaskList();
        this.fetchActiveTaskCount();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.deleteTask(id)
        .then((result) => {
          result.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Отправить запрос на изменение статуса задачи
     * @param {number} id Идентификатор задачи
     * @param {string} status Название статуса
     */
    onChangeStatus(id, status) {
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.isTaskDisplayOpen = false;
        this.fetchTaskList();
        this.fetchActiveTaskCount();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.changeStatus(id, status)
        .then((result) => {
          result.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Отправить запрос на добавление комментария к задаче задачи
     * @param {number} id Идентификатор задачи
     * @param {string} commentText Текст комментария
     */
    onSubmitComment(id, commentText) {
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.commentViolations = {};
        this.showTaskDisplay(this.selectedTask.id);
      };
      const onFail = (error) => {
        this.commentViolations = error.allCauses();
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.createTaskComment(id, commentText)
        .then((result) => {
          result.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Показать форму редактирования
     */
    showEditingForm() {
      this.editingTask = this.selectedTask;
      this.isTaskEditingOpen = true;
      this.isTaskDisplayOpen = false;
    },
    /**
     * @description Закрыть форму редактирования
     */
    closeEditingForm() {
      this.editViolations = {};
      this.isTaskEditingOpen = false;
      this.needConfirmEditingForm = false;
    },
  },
};

/**
 * Миксин отображение задачи
 */
const taskDisplayMixin = {
  data() {
    return {
      /** Открыт ли показ задачи */
      isTaskDisplayOpen: false,
      /** Выбранная задача для показа */
      selectedTask: {},
    };
  },
  methods: {
    /**
     * @description Показать задачу в боковой модалке
     * @param {number} taskId Идентификатор задачи
     */
    showTaskDisplay(taskId) {
      const onSuccess = (task) => {
        this.isTaskDisplayOpen = true;
        this.selectedTask = task;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.getTaskById(taskId)
        .then((response) => {
          response.fold(onFail, onSuccess);
        });
    },
  },
};

/**
 * Миксин формы создания задачи
 */
const formTaskCreationMixin = {
  data() {
    return {
      /** Нужно подтверждение закрытия формы создания */
      needConfirmCreationForm: false,
      /** Открыта ли форма создания задачи */
      isTaskCreationFormOpen: false,
      /** Ошибки при создании */
      createViolations: {},
    };
  },
  methods: {
    /**
     * @description Отправить запрос на создание задачи
     * @param {TaskRequest} request
     */
    onSendCreateRequest(request) {
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.closeTaskCreationForm();
        this.fetchTaskList();
      };
      const onFail = (error) => {
        this.createViolations = error.allCauses();
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.createTask(request)
        .then((result) => {
          result.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Показать форму создания задачи
     */
    showTaskCreationForm() {
      this.isTaskCreationFormOpen = true;
    },
    /**
     * @description Закрыть форму создания задачи
     */
    closeTaskCreationForm() {
      this.isTaskCreationFormOpen = false;
      this.createViolations = {};
    },
  },
};

const titleToWieghtMap = {
  all: 5,
  new: 4,
  work: 3,
  completed: 2,
  canceled: 1,
};

export default {
  components: {
    DateRangeWithPresets,
    AvailableSection,
    TaskDisplay,
    FormTaskCreation,
    FormTaskEditing,
    ConfirmSideDialog,
    SideSlideTransition,
    TaskTable,
  },
  created() {
    this.setUserEmployeeFilterId();
    this.fetchTaskList();
    this.fetchOptionsList(this.filter.patientId);
  },
  computed: {
    /**
     * Фильтр
     * @returns TaskFilterRequest
     */
    filter: {
      get() {
        return this.$store.getters.getFilter;
      },
    },
    performerId: {
      get() {
        const { performerId } = this.$store.getters.getFilter;
        return performerId;
      },
    },
  },
  watch: {
    selectedTaskStatus: { handler: 'filterTaskListByStatus' },
  },
  data() {
    return {
      /** Список задач */
      taskList: [],
      /** Список исполнителей */
      performerList: [],
      /** Список пациентов */
      patientList: [],
      /** Список ролей */
      roleList: [],
      /** Список исполнителей для фильтра */
      filterPerformerList: [],
      /** Список пациентов для фильтра */
      filterPatientList: [],
      /** Список ролей для фильтра */
      filterRoleList: [],
      /** Тип сортировки */
      sortOrder: {
        type: 'createdAt',
        order: 'desc',
      },
      /** Выбранный статус задач */
      selectedTaskStatus: '',
      /** Группа статусов задач */
      taskStatusGroup: [],
      /** Отфильтрованный список задач */
      filteredTaskList: [],
      /** Выбранный промежуток даты постановки задачи */
      createdAtDate: [],
      /** Выбранный промежуток даты дедлайна */
      deadLineDate: [],
      /**
       * @description Опции для ролей
       * @type Array
       */
      roleOptions: [],
      /**
       * @description Ожидание завершения запроса.
       * @type {boolean}
       */
      isAwaitRequest: false,
    };
  },
  methods: {
    /**
     * @description Обработать событие поиска опции.
     * @param {string|null} searchText
     */
    onSearchOptionInput(searchText) {
      const preparedSearchText = this.prepareQuery(searchText);
      if (preparedSearchText === null) {
        return;
      }
      this.fetchOptionsList(preparedSearchText);
    },
    /**
     * @description Обновить фильтр задач
     * @returns {void}
     */
    updateTaskFilter() {
      this.$store.dispatch(STORE_TASK_FILTER, this.filter);
      this.fetchTaskList();
    },
    mapStatus,
    /**
     * @description Получить количество активных задач.
     * @return {void}
     */
    fetchActiveTaskCount() {
      this.$taskGateway.getActiveTaskCount()
        .then((result) => {
          result.forEach(({ count }) => {
            this.$store.commit(ACTIVE_TASK_COUNT, count);
          });
        });
    },
    /**
     * @description Затянуть список задач
     */
    fetchTaskList() {
      const onSuccess = (tasks) => {
        this.taskList = tasks;
        this.updateTaskStatusGroup();
        this.filterTaskListByStatus();
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      this.isAwaitRequest = true;
      const request = new ViewTaskListRequest(
        this.sortOrder,
        this.filter.authorId,
        this.filter.performerId,
        this.filter.patientId,
        this.filter.roleName,
        this.filter.createPeriod,
        this.filter.deadlinePeriod,
      );
      this
        .$taskGateway
        .getTaskList(request)
        .then(
          (result) => {
            result.fold(onFail, onSuccess);
            this.isAwaitRequest = false;
          },
        );
    },
    /**
     * @description Затянуть список исполнителей
     * @param {string} searchPatientQuery
     */
    fetchOptionsList: debounce(function _(searchPatientQuery = '') {
      const onSuccess = ({
        performers,
        patients,
        roleOptions,
      }) => {
        this.performerList = performers;
        this.patientList = patients;
        this.roleList = roleOptions;
        this.filterPerformerList = this.performerList;
        this.filterPatientList = this.patientList;
        this.filterRoleList = this.roleList;
        this.roleOptions = roleOptions;
      };
      this.$taskGateway.getTaskOptions(searchPatientQuery ?? '')
        .then((result) => {
          result.fold(() => {
          }, onSuccess);
        });
    }, 500),
    /**
     * @description Сортировать список задач
     */
    onSortTaskList(sort) {
      this.sortOrder = sort;
      this.fetchTaskList();
    },
    /**
     * @description Сортировать список задач по статусу
     */
    filterTaskListByStatus() {
      if (this.selectedTaskStatus && this.selectedTaskStatus !== 'all') {
        this.filteredTaskList = this.taskList.filter(
          (task) => task.status === this.selectedTaskStatus,
        );
      } else {
        this.filteredTaskList = this.taskList;
      }
    },
    /**
     * @description Обновить сгрупированный список статусов задач
     */
    updateTaskStatusGroup() {
      this.taskStatusGroup = [];
      if (this.taskList.length === 0) {
        return;
      }
      this.taskList.forEach((task) => {
        if (this.taskStatusGroup.filter((status) => status.title === task.status).length) {
          this.taskStatusGroup.find((status) => status.title === task.status).count += 1;
        } else {
          this.taskStatusGroup.push({
            title: task.status,
            count: 1,
          });
        }
      });
      this.taskStatusGroup.push({
        title: 'all',
        count: this.taskList.length,
      });
      this.taskStatusGroup.sort((a, b) => titleToWieghtMap[b.title] - titleToWieghtMap[a.title]);
    },
    setUserEmployeeFilterId() {
      if (!this.$store.getters.isOwner && this.filter.isEmpty()) {
        this.filter.performerId = this.$store.getters.getUserEmployeeId ?? 0;
      }
    },
  },
  mixins: [
    notifierMixin,
    searchPatientMixin,
    formTaskCreationMixin,
    taskDisplayMixin,
    taskEditingMixin,
  ],
  name: 'TaskList',
};
</script>

<style lang="scss" scoped>
@use "@/scss/buttons";

.task-list {
  display: flex;
  height: 100%;
  width: 100%;
  overflow-x: auto;

  &__body {
    padding: 38px 20px 48px 23px;
    width: 100%;
    overflow-y: scroll;
    overflow-x: hidden;
  }

  &__header {
    display: flex;
    flex-flow: row wrap;
    gap: 20px;
    justify-content: space-between;
    align-items: center;
    margin: 0 0 43px 0;
  }

  &__filters {
    display: grid;
    gap: 20px;
    align-items: center;
    max-width: 1800px;
    margin: 0 0 43px 0;

    ::v-deep .date-range-with-presets {
      flex-wrap: wrap;

      &__search {
        min-width: 250px;
      }
    }

    .date-range-title {
      display: block;
      margin: 0 0 2px 0;
      color: var(--unfocus-color);
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
    }

    &__selects {
      display: grid;
      grid-template-columns: 25% 25% 25% 20%;
      justify-content: space-between;
      align-items: center;
      gap: 10px 20px;
    }

    &__dates {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      align-items: center;
      justify-content: space-between;
      gap: 20px 30px;
      max-width: 1300px;
    }
  }

  &__groups {
    display: flex;
    flex-flow: row wrap;
    gap: 6px;
    margin: 10px 0;
  }

  &__group {
    display: flex;
    align-items: center;

    & > input {
      position: absolute;
      z-index: -1;
      opacity: 0;

      &:checked + span {
        border-color: var(--action-color);
        border-width: 2px;
        padding: 7px 14px;
      }
    }

    & > span {
      display: flex;
      align-items: center;
      color: var(--reading-color);
      font-size: 14px;
      padding: 8px 15px;
      border: 1px solid #c2cfe0;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        border-color: var(--action-color);
        border-width: 2px;
        padding: 7px 14px;
      }

      &:before {
        content: '';
      }

      &.all:before {
        content: none;
      }

      &.canceled:before {
        width: 20px;
        height: 20px;
        margin-right: 5px;
        background-image: url("@/assets/img/exit-red.svg");
        background-repeat: no-repeat;
        background-size: contain;
      }

      &.completed:before {
        height: 20px;
        width: 20px;
        margin-right: 10px;
        background-image: url("@/assets/img/checked/all-checked-green.svg");
        background-repeat: no-repeat;
        background-size: contain;
      }

      &.new:before {
        height: 6px;
        width: 6px;
        margin-right: 10px;
        background-color: #abb1a3;
        border-radius: 50%;
      }

      &.work:before {
        height: 15px;
        width: 15px;
        margin-right: 10px;
        background-image: url("@/assets/img/checked/checked-green-light.svg");
        background-repeat: no-repeat;
        background-size: contain;
      }
    }
  }

  &__autocomplete {
    &.v-input .v-label {
      top: 3px
    }
  }

  &__button {
    &-add {
      @include buttons.btn-blue(187px, 48px, 16px);
    }
  }
}

@media screen and (max-width: 1400px) {
  .task-list {
    &__filters {
      &__dates {
        grid-template-columns: 1fr;
        align-items: start;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .task-list {
    &__filters {
      &__selects {
        display: grid;
        grid-template-columns: 1fr 1fr;
      }
    }
  }
}

@media screen and (max-width: 440px) {
  .task-list {
    &__filters {
      &__selects {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>
